import { useCallback, useState } from 'react'
import { GridApi } from 'ag-grid-community'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import {
  CriticalPathLinkData,
  CriticalPathDragContextValue,
  useCriticalPathContext,
} from './CriticalPathContext'
import { useCriticalPathDragInteraction } from './useCriticalPathDragInteraction'
import {
  CpPopupState,
  useCriticalPathLinePopup,
} from './useCriticalPathLinePopup'
import { DatePosition, getDateForPercentX } from '../hooks/useDatePositions'
import { categorizeLinks, FallbackLinkInfo } from '../utils/visibilityFilters'
import {
  getWbsItemDisplayName,
  getWbsItemIconUrl,
  getWbsItemScheduledDate,
} from '../utils/linkInfoPopupUtils'
import { LinkEditorResult } from '../WbsItemLink/LinkEditorPopup'
import { WbsItemLinkSummary } from '../WbsItemLink/WbsItemLinkDialog'
import { GanttRowItem } from '../../GanttRowItem'
import { formatDateForApi } from '../../../../../../../../../../utils/date'

type UseGanttCriticalPathInteractionParams = {
  api: GridApi
  dataPath?: string
  rowIndex: number
  isProcess: boolean
  wbsItemUuid: string | null
  barEndpoints: { startX: number | null; endX: number | null }
  datePositions: DatePosition[]
  isHovered: boolean
  isDependencyDragging: boolean
  getGanttRowItem?: (data: unknown) => GanttRowItem | undefined
}

type CpListDialogState = {
  items: WbsItemLinkSummary[]
}

type UseGanttCriticalPathInteractionResult = {
  criticalPathLinks: CriticalPathLinkData[]
  partialCPs: FallbackLinkInfo<CriticalPathLinkData>[]
  fallbackCPs: FallbackLinkInfo<CriticalPathLinkData>[]
  isCriticalPathDragging: boolean
  isCriticalPathDropTarget: boolean
  showCriticalPathDropHighlight: boolean
  showCriticalPathPreDragCircle: boolean
  criticalPathPreDragInfo: {
    date: CalendarDateVO
    pixelX: number
    position: 'top' | 'bottom'
  } | null
  isCriticalPathRowHovered: boolean
  setIsCriticalPathRowHovered: (v: boolean) => void
  criticalPathDropPointPercent: number | null
  criticalPathDragContext: CriticalPathDragContextValue | null
  handleCriticalPathDropMouseUp: (event: React.MouseEvent) => void
  handleCriticalPathDropRowMouseMove: (e: React.MouseEvent) => void
  cpPopup: CpPopupState | null
  handleCriticalPathLineClick: (
    link: CriticalPathLinkData,
    event: React.MouseEvent
  ) => void
  handleDeleteCriticalPath: () => void
  handleCriticalPathUpdate: (result: LinkEditorResult) => void
  closeCriticalPathPopup: () => void
  handleFallbackCpClick: (
    info: FallbackLinkInfo<CriticalPathLinkData>,
    event: React.MouseEvent
  ) => void
  handleCellLeave: () => void
  handleCellMouseMove: (
    clientX: number,
    clientY: number,
    containerRect: DOMRect,
    processBarHalfHeight: number,
    isOverThumb: boolean
  ) => void
  handleCellMouseDown: (
    e: MouseEvent,
    containerRect: DOMRect,
    processBarHalfHeight: number,
    circleSize: number
  ) => void
  cpListDialog: CpListDialogState | null
  handleFallbackCpGroupClick: (
    infos: FallbackLinkInfo<CriticalPathLinkData>[],
    event: React.MouseEvent
  ) => void
  handleCpDialogUpdate: (
    uuid: string,
    updates: {
      color?: string
      description?: string
      fromDate?: string
      toDate?: string
    }
  ) => void
  handleCpDialogRemove: (uuid: string) => void
  closeCpListDialog: () => void
}

// MUI Slider は日付ラベル(valueLabel)をサムの子要素として描画するため、
// サムのホバー範囲はバーの外（⊕の判定域）まで伸びる。両方が同時に反応しないよう、
// サム配下にカーソルがある間は⊕の判定を止める。
export const isOverSliderThumb = (e: MouseEvent): boolean =>
  e.target instanceof Element && !!e.target.closest('.MuiSlider-thumb')

export const useGanttCriticalPathInteraction = (
  params: UseGanttCriticalPathInteractionParams
): UseGanttCriticalPathInteractionResult => {
  const {
    api,
    dataPath,
    rowIndex,
    isProcess,
    wbsItemUuid,
    barEndpoints,
    datePositions,
    isHovered,
    isDependencyDragging,
    getGanttRowItem,
  } = params
  const prefix = dataPath ? `${dataPath}.` : ''

  // Critical path context: categorize into visible lines, partial lines, and fallback icons
  const criticalPathContext = useCriticalPathContext()
  const {
    visible: criticalPathLinks,
    partial: partialCPs,
    fallback: fallbackCPs,
  } = categorizeLinks(criticalPathContext?.criticalPathLinks ?? [], api)

  // Critical path drag interaction
  const {
    isCriticalPathDragging,
    isCriticalPathDropTarget,
    showCriticalPathDropHighlight,
    showPreDragCircle: showCriticalPathPreDragCircle,
    preDragInfo: criticalPathPreDragInfo,
    isCriticalPathRowHovered,
    setIsCriticalPathRowHovered,
    handleBarMouseMove: handleCriticalPathBarMouseMove,
    clearPreDragInfo,
    handleDropMouseUp: handleCriticalPathDropMouseUp,
    handleDropRowMouseMove: handleCriticalPathDropRowMouseMove,
    dropPointPercent: criticalPathDropPointPercent,
    criticalPathDragContext,
  } = useCriticalPathDragInteraction({
    rowIndex,
    isProcess,
    wbsItemUuid,
    barEndpoints,
    datePositions,
    isHovered,
    isDependencyDragging,
  })

  // Critical path line popup
  const {
    cpPopup,
    handleLineClick: handleCriticalPathLineClick,
    handleDelete: handleDeleteCriticalPath,
    handleUpdate: handleCriticalPathUpdate,
    closePopup: closeCriticalPathPopup,
    openPopup: openCpPopup,
  } = useCriticalPathLinePopup(api, dataPath, getGanttRowItem)

  // WBS item info map from context (server-sourced, avoids full-grid iteration)
  const cpWbsItemInfoMap = criticalPathContext?.wbsItemInfoMap

  // Fallback icon click handler (resolve names/icons for hidden endpoints)
  const handleFallbackCpClick = useCallback(
    (info: FallbackLinkInfo<CriticalPathLinkData>, event: React.MouseEvent) => {
      const { link, from, to } = info
      let fromName: string
      let fromIconUrl: string | undefined
      let fromScheduled: { startDate?: string; endDate?: string } | undefined
      if (from.isFallback) {
        const resolved = cpWbsItemInfoMap?.get(link.fromWbsItemUuid)
        fromName = resolved?.displayName ?? ''
        fromIconUrl = resolved?.iconUrl
      } else {
        fromName = getWbsItemDisplayName(api, from.rowIndex, prefix)
        fromIconUrl = getWbsItemIconUrl(api, from.rowIndex, prefix)
        fromScheduled = getWbsItemScheduledDate(
          api,
          from.rowIndex,
          prefix,
          getGanttRowItem
        )
      }
      let toName: string
      let toIconUrl: string | undefined
      let toScheduled: { startDate?: string; endDate?: string } | undefined
      if (to.isFallback) {
        const resolved = cpWbsItemInfoMap?.get(link.toWbsItemUuid)
        toName = resolved?.displayName ?? ''
        toIconUrl = resolved?.iconUrl
      } else {
        toName = getWbsItemDisplayName(api, to.rowIndex, prefix)
        toIconUrl = getWbsItemIconUrl(api, to.rowIndex, prefix)
        toScheduled = getWbsItemScheduledDate(
          api,
          to.rowIndex,
          prefix,
          getGanttRowItem
        )
      }
      const toDateRangeFn = (
        s: { startDate?: string; endDate?: string } | undefined
      ): { min: Date; max: Date } | null => {
        if (!s?.startDate || !s?.endDate) return null
        return { min: new Date(s.startDate), max: new Date(s.endDate) }
      }
      openCpPopup({
        link,
        fromName,
        toName,
        fromIconUrl,
        toIconUrl,
        fromProjectName: cpWbsItemInfoMap?.get(link.fromWbsItemUuid)
          ?.projectName,
        toProjectName: cpWbsItemInfoMap?.get(link.toWbsItemUuid)?.projectName,
        fromProjectIconUrl: cpWbsItemInfoMap?.get(link.fromWbsItemUuid)
          ?.projectIconUrl,
        toProjectIconUrl: cpWbsItemInfoMap?.get(link.toWbsItemUuid)
          ?.projectIconUrl,
        fromDateRange: toDateRangeFn(fromScheduled),
        toDateRange: toDateRangeFn(toScheduled),
      })
    },
    [api, prefix, openCpPopup, cpWbsItemInfoMap, getGanttRowItem]
  )

  const handleCellLeave = useCallback(() => {
    clearPreDragInfo()
  }, [clearPreDragInfo])

  const handleCellMouseMove = useCallback(
    (
      clientX: number,
      clientY: number,
      containerRect: DOMRect,
      processBarHalfHeight: number,
      isOverThumb: boolean
    ) => {
      if (!isProcess || isCriticalPathDragging || isDependencyDragging) return
      if (isOverThumb) {
        clearPreDragInfo()
        return
      }
      const centerY = containerRect.top + containerRect.height * 0.5
      if (Math.abs(clientY - centerY) < processBarHalfHeight) {
        clearPreDragInfo()
        return
      }
      handleCriticalPathBarMouseMove(clientX, clientY, containerRect)
    },
    [
      isProcess,
      isCriticalPathDragging,
      isDependencyDragging,
      handleCriticalPathBarMouseMove,
      clearPreDragInfo,
    ]
  )

  // Cell-level mousedown handler for critical path drag start via indicator icon.
  // The indicator icon is rendered with pointer-events: none to avoid blocking
  // the MUI Slider. Instead, we detect clicks on the icon position at the cell level.
  const handleCellMouseDown = useCallback(
    (
      e: MouseEvent,
      containerRect: DOMRect,
      processBarHalfHeight: number,
      circleSize: number
    ) => {
      if (isOverSliderThumb(e)) return
      // 線を引けるかは行が process でかつ有効な行であるかで判定する。
      // wbsItemUuid（保存用の識別子）は drop 時に link へ格納するだけで、
      // ドラッグ開始の可否には用いない（未取得でも線は引けるべき）
      if (
        !isProcess ||
        !criticalPathPreDragInfo ||
        isCriticalPathDragging ||
        isDependencyDragging ||
        !criticalPathDragContext ||
        rowIndex < 0
      ) {
        return
      }
      const { position, date } = criticalPathPreDragInfo

      // Bar-body exclusion: reject mousedown within the bar body area.
      const centerY = containerRect.top + containerRect.height * 0.5
      if (Math.abs(e.clientY - centerY) < processBarHalfHeight) return

      // X check: verify click is in the same date cell as the pre-drag indicator.
      // The indicator snaps to the date cell center, so the mouse may be anywhere
      // within that cell. Checking the date cell avoids a strict pixel tolerance
      // that fails for wider cells (week/month/quarter units).
      const relativeX = e.clientX - containerRect.left
      const percentX = (relativeX / containerRect.width) * 100
      const clickDate = getDateForPercentX(datePositions, percentX)
      if (!clickDate || !clickDate.isEqual(date)) return

      // Y check: verify click is on the indicator icon at the bar edge.
      // バー本体側は上のガードで既に除外しているので、行端まで押下可能にする。
      // ここを circleSize / 2 に狭めると⊕が見えているのに掴めない帯ができる。
      const isTop = position === 'top'
      const iconCenterY = isTop
        ? containerRect.top + containerRect.height * 0.5 - processBarHalfHeight
        : containerRect.top + containerRect.height * 0.5 + processBarHalfHeight
      if (Math.abs(e.clientY - iconCenterY) > circleSize) return

      e.preventDefault()
      e.stopPropagation()
      criticalPathDragContext.startDrag({
        fromRowIndex: rowIndex,
        fromDate: date,
        startX: e.clientX,
        startY: e.clientY,
      })

      // Attach a one-shot mouseup listener to ensure the drag always ends.
      // CriticalPathDragOverlay attaches its document mouseup via a useEffect,
      // but if the user releases the mouse before that effect runs (race condition),
      // the drag state gets stuck. endDrag() is idempotent, so duplicate calls are safe.
      const onMouseUp = () => {
        document.removeEventListener('mouseup', onMouseUp)
        setTimeout(() => criticalPathDragContext.endDrag(), 0)
      }
      document.addEventListener('mouseup', onMouseUp)
    },
    [
      isProcess,
      criticalPathPreDragInfo,
      isCriticalPathDragging,
      isDependencyDragging,
      criticalPathDragContext,
      rowIndex,
      datePositions,
    ]
  )

  // ============================================================================
  // List dialog for grouped fallback icons
  // ============================================================================
  const [cpListDialog, setCpListDialog] = useState<CpListDialogState | null>(
    null
  )

  const resolveCpName = useCallback(
    (info: FallbackLinkInfo<CriticalPathLinkData>) => {
      const { link, from, to } = info
      let fromName: string
      let fromIconUrl: string | undefined
      let fromScheduled: { startDate?: string; endDate?: string } | undefined
      if (from.isFallback) {
        const resolved = cpWbsItemInfoMap?.get(link.fromWbsItemUuid)
        fromName = resolved?.displayName ?? ''
        fromIconUrl = resolved?.iconUrl
      } else {
        fromName = getWbsItemDisplayName(api, from.rowIndex, prefix)
        fromIconUrl = getWbsItemIconUrl(api, from.rowIndex, prefix)
        fromScheduled = getWbsItemScheduledDate(
          api,
          from.rowIndex,
          prefix,
          getGanttRowItem
        )
      }
      let toName: string
      let toIconUrl: string | undefined
      let toScheduled: { startDate?: string; endDate?: string } | undefined
      if (to.isFallback) {
        const resolved = cpWbsItemInfoMap?.get(link.toWbsItemUuid)
        toName = resolved?.displayName ?? ''
        toIconUrl = resolved?.iconUrl
      } else {
        toName = getWbsItemDisplayName(api, to.rowIndex, prefix)
        toIconUrl = getWbsItemIconUrl(api, to.rowIndex, prefix)
        toScheduled = getWbsItemScheduledDate(
          api,
          to.rowIndex,
          prefix,
          getGanttRowItem
        )
      }
      return {
        fromName,
        fromIconUrl,
        toName,
        toIconUrl,
        fromScheduled,
        toScheduled,
      }
    },
    [api, prefix, cpWbsItemInfoMap, getGanttRowItem]
  )

  const handleFallbackCpGroupClick = useCallback(
    (infos: FallbackLinkInfo<CriticalPathLinkData>[]) => {
      const items: WbsItemLinkSummary[] = infos.map(info => {
        const {
          fromName,
          fromIconUrl,
          toName,
          toIconUrl,
          fromScheduled,
          toScheduled,
        } = resolveCpName(info)
        return {
          uuid: info.link.uuid,
          fromWbsItemUuid: info.link.fromWbsItemUuid,
          toWbsItemUuid: info.link.toWbsItemUuid,
          fromName,
          toName,
          fromIconUrl,
          toIconUrl,
          fromDate: formatDateForApi(info.link.fromDate.date),
          toDate: formatDateForApi(info.link.toDate.date),
          fromScheduledDate: fromScheduled,
          toScheduledDate: toScheduled,
          color: info.link.color ?? null,
          description: info.link.description ?? null,
          createdAt: info.link.createdAt,
          createdBy: info.link.createdBy,
          updatedAt: info.link.updatedAt,
          updatedBy: info.link.updatedBy,
        }
      })
      setCpListDialog({ items })
    },
    [resolveCpName]
  )

  const handleCpDialogUpdate = useCallback(
    (
      uuid: string,
      updates: {
        color?: string
        description?: string
        fromDate?: string
        toDate?: string
      }
    ) => {
      if (updates.color !== undefined || updates.description !== undefined) {
        criticalPathContext?.updateCriticalPathLinkProps(uuid, {
          color: updates.color,
          description: updates.description,
        })
      }
      if (updates.fromDate !== undefined) {
        criticalPathContext?.updateCriticalPathLinkDate(
          uuid,
          'fromDate',
          CalendarDateVO.of(new Date(updates.fromDate))
        )
      }
      if (updates.toDate !== undefined) {
        criticalPathContext?.updateCriticalPathLinkDate(
          uuid,
          'toDate',
          CalendarDateVO.of(new Date(updates.toDate))
        )
      }
      setCpListDialog(prev => {
        if (!prev) return null
        return {
          items: prev.items.map(item =>
            item.uuid === uuid
              ? {
                  ...item,
                  color: updates.color ?? item.color,
                  description: updates.description ?? item.description,
                  fromDate: updates.fromDate ?? item.fromDate,
                  toDate: updates.toDate ?? item.toDate,
                }
              : item
          ),
        }
      })
    },
    [criticalPathContext]
  )

  const handleCpDialogRemove = useCallback(
    (uuid: string) => {
      criticalPathContext?.removeCriticalPathLink(uuid)
      setCpListDialog(prev => {
        if (!prev) return null
        const remaining = prev.items.filter(item => item.uuid !== uuid)
        if (remaining.length === 0) return null
        return { items: remaining }
      })
    },
    [criticalPathContext]
  )

  const closeCpListDialog = useCallback(() => {
    setCpListDialog(null)
  }, [])

  return {
    criticalPathLinks,
    partialCPs,
    fallbackCPs,
    isCriticalPathDragging,
    isCriticalPathDropTarget,
    showCriticalPathDropHighlight,
    showCriticalPathPreDragCircle,
    criticalPathPreDragInfo,
    isCriticalPathRowHovered,
    setIsCriticalPathRowHovered,
    criticalPathDropPointPercent,
    criticalPathDragContext,
    handleCriticalPathDropMouseUp,
    handleCriticalPathDropRowMouseMove,
    cpPopup,
    handleCriticalPathLineClick,
    handleDeleteCriticalPath,
    handleCriticalPathUpdate,
    closeCriticalPathPopup,
    handleFallbackCpClick,
    handleCellLeave,
    handleCellMouseMove,
    handleCellMouseDown,
    cpListDialog,
    handleFallbackCpGroupClick,
    handleCpDialogUpdate,
    handleCpDialogRemove,
    closeCpListDialog,
  }
}
